import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, Card, Col, Overlay, OverlayTrigger, Row, Tooltip } from 'react-bootstrap-5';
import { connect } from 'react-redux';
import { setOwners } from 'bundles/connect/components/store/actions/owners';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faArrowRight, faCaretDown, faEnvelope, faPhone, faUser
} from '@fortawesome/pro-solid-svg-icons';
import Ownership from './owners/ownership';
import { ACTIVITY_TYPE_CALL, ACTIVITY_TYPE_EMAIL } from '../../constants';
import pluralize from 'pluralize';
import { fetchLastFetchedAt } from '../../helpers/prospect_api';
import EmptyState from '../empty_state';
import { faCircleCheck, faUserCircle } from '@fortawesome/pro-regular-svg-icons';
import AddContact from './owners/add_contact';

import NewActivityForm from './activity_forms/new_activity_form';
import UnlockOwners from '../off_market_properties/owners/unlock_owners';
import type { CallTemplate, Ownerships, OwnershipsResponse } from 'bundles/connect/types';

type Props = {
  callTemplates: CallTemplate[],
  connectPropertyId: number,
  insightsPropertyId: string,
  owners?: Ownerships[] | null,
  setOwners: (ownershipsResponse: OwnershipsResponse) => void
}

function ActivitySection(
  {
    callTemplates,
    connectPropertyId,
    insightsPropertyId,
    owners,
    setOwners
  }: Props
) {
  const [ownerPosition, setOwnerPosition] = useState(0);
  const [showContactSelect, setShowContactSelect] = useState(false);
  const triggerRef = useRef(null);
  const [initialActivity, setInitialActivity] = useState({});
  const [remainingCredits, setRemainingCredits] = useState(0);

  const shownOwner = owners ? owners[ownerPosition] : undefined;

  useEffect(() => {
    if (owners && owners.length > 0) {
      setOwnerPosition(0);
    }
  }, [owners?.length]);

  useEffect(() => {
    if (owners && owners.length == 0) {
      fetchLastFetchedAt(insightsPropertyId).then((res) => {
        if (res.lastFetched) {
          setRemainingCredits(res.remainingCredits);
        }
      });
    }
  }, [owners]);

  const handleLogActivityClick = (activityType, contactMethod) => {
    let activityValue;
    let activityValueKey;
    if (activityType === ACTIVITY_TYPE_EMAIL) {
      activityValue = contactMethod.email;
      activityValueKey = 'email';
    } else if (activityType === ACTIVITY_TYPE_CALL) {
      activityValue = contactMethod.number;
      activityValueKey = 'phoneNumber';
    }

    setInitialActivity({ type: activityType, [activityValueKey]: activityValue });
  };

  const renderLogActivity = (contactMethodKey, activityValue) => {
    let tooltipLabel;
    let activityType;
    if (contactMethodKey === 'emails') {
      tooltipLabel = 'Log Email';
      activityType = ACTIVITY_TYPE_EMAIL;
    } else if (contactMethodKey === 'phoneNumbers') {
      tooltipLabel = 'Log Call';
      activityType = ACTIVITY_TYPE_CALL;
    }

    return (
      <OverlayTrigger offset={[0, 5]} overlay={<Tooltip>{tooltipLabel}</Tooltip>}>
        <Button
          className="rounded"
          size="sm"
          variant="outline"
          onClick={() => handleLogActivityClick(activityType, activityValue)}
        >
          <FontAwesomeIcon
            icon={activityType === ACTIVITY_TYPE_EMAIL ? faEnvelope : faPhone}
            size="sm"
          />
        </Button>
      </OverlayTrigger>
    );
  };

  const togglePrevContact = () => {
    if (!owners) {
      return;
    }

    setInitialActivity({});
    if (ownerPosition === 0) {
      setOwnerPosition(owners.length - 1);
    } else {
      setOwnerPosition(ownerPosition - 1);
    }
  };

  const toggleNextContact = () => {
    if (!owners) {
      return;
    }

    setInitialActivity({});
    if (ownerPosition === owners.length - 1) {
      setOwnerPosition(0);
    } else {
      setOwnerPosition(ownerPosition + 1);
    }
  };

  const handleOwnerSelect = (selectedOwner) => {
    if (!owners) {
      return;
    }

    setOwnerPosition(owners.findIndex(owner => owner.contact.id === selectedOwner.contact.id));
    setShowContactSelect(false);
  };

  const popoverCard = (
    <Card className="shadow" style={{ maxWidth: '500px', minWidth: '320px' }}>
      {owners && (
        <>
          <Card.Header className="p-3">
            <div className="d-flex justify-content-between">
              <div>Contacts</div>
              <div className="text-muted">{owners.length} contacts</div>
            </div>
          </Card.Header>
          <Card.Body className="p-3">
            {owners.map(owner => (
              <div
                className="d-flex clickable p-2 align-items-center gap-2 justify-content-between"
                key={owner.id}
                onClick={() => handleOwnerSelect(owner)}
              >
                <div>
                  <div className="d-flex gap-2 align-items-center ">
                    <FontAwesomeIcon icon={faUserCircle} />
                    <div>{owner.contact.name}</div>
                    {!owner.verified ? null : (
                      <Badge bg="" className="badge-accent badge-secondary fs-small py-0-5 px-2">
                        <div className="d-flex align-items-center gap-1">
                          <FontAwesomeIcon icon={faCircleCheck} size="sm" />
                          <div>Verified</div>
                        </div>
                      </Badge>
                    )}
                  </div>
                </div>
                {owner.label && <div className="text-muted small">{owner.label}</div>}
              </div>
            ))}
          </Card.Body>
        </>
      )}
    </Card>
  );

  if (owners === null) {
    return (
      <Card className="shadow">
        <Card.Body>
          <UnlockOwners
            insightsPropertyId={insightsPropertyId}
            remainingCredits={remainingCredits}
            setRemainingCredits={setRemainingCredits}
            onUnlock={setOwners}
          />
        </Card.Body>
      </Card>
    );
  }

  if (!shownOwner) {
    return (
      <Card className="shadow">
        <Card.Body>
          <EmptyState
            icon={faUser}
            text={'Manually add your own contact to use the call console'}
            title={'No Contacts Found'}
          />
          <div className="text-center">
            <AddContact showLabel />
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow">
      <>
        <Card.Header>
          <Row className="align-items-center gy-2">
            <Col>
              <div className="d-flex align-items-center gap-2">
                <div className="lh-1">
                  <h5 className="fw-bold mb-0 privacy-mask">{shownOwner.contact.name} </h5>
                  {shownOwner.label && <div className="text-muted">{shownOwner.label}</div>}
                </div>
                {!shownOwner.verified ? null : (
                  <Badge bg="" className="badge-accent badge-secondary fs-small py-0-5 px-2">
                    <div className="d-flex align-items-center gap-1">
                      <FontAwesomeIcon icon={faCircleCheck} />
                      Verified Owner
                    </div>
                  </Badge>
                )}
              </div>
            </Col>
            {owners && (
              <Col md="auto" xs={12}>
                <>
                  <div className="d-flex align-items-center gap-2">
                    <AddContact />
                    {owners.length > 1 && (
                      <>
                        <div
                          className="d-flex clickable align-items-center gap-1"
                          ref={triggerRef}
                          onClick={() => setShowContactSelect(true)}
                        >
                          {ownerPosition + 1} of {owners.length} {pluralize('Contact', owners.length)}
                          <FontAwesomeIcon icon={faCaretDown} size="sm" />
                        </div>
                        <Button
                          variant="outline-primary"
                          onClick={togglePrevContact}
                        >
                          <FontAwesomeIcon icon={faArrowLeft} />
                        </Button>
                        <Button
                          variant="outline-primary"
                          onClick={toggleNextContact}
                        >
                          <FontAwesomeIcon icon={faArrowRight} />
                        </Button>
                        <Overlay
                          placement="bottom"
                          rootClose
                          show={showContactSelect}
                          target={triggerRef.current}
                          onHide={() => setShowContactSelect(false)}
                        >
                          {popoverCard}
                        </Overlay>
                      </>
                    )}
                  </div>
                </>
              </Col>
            )}
          </Row>
        </Card.Header>
        <Card.Body>
          <Row className="g-6">
            <Col md={4}>
              <Ownership owner={shownOwner} renderLogActivity={renderLogActivity} showOwnerName={false} />
            </Col>
            <Col md={8}>
              <NewActivityForm
                callTemplates={callTemplates}
                connectPropertyId={connectPropertyId}
                initialActivity={initialActivity}
                owner={shownOwner}
              />
            </Col>
          </Row>
        </Card.Body>
      </>
    </Card>
  );
}

const mapStateToProps = ({ callConsoleData }) => ({
  insightsPropertyId: callConsoleData.insightsProperty.propertyId,
  owners: callConsoleData.owners
});
export default connect(mapStateToProps, { setOwners })(ActivitySection);
